import React, { useCallback, useEffect, useState } from 'react';

import { BaseProvider, LightTheme } from 'baseui';
import { PLACEMENT, ToasterContainer } from 'baseui/toast';

import LogRocket from 'logrocket';
import { Provider } from 'mobx-react';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import NextNprogress from 'nextjs-progressbar';
import { Provider as StyletronProvider } from 'styletron-react';

import { getCurrentUserAction, getTwilioToken } from '@gtintel/platform-api';
import {
  ADMIN_ROLE,
  CLIENT_ROLE,
  COOKIE_NAMES,
  PARTNER_ROLE,
} from '@gtintel/platform-constants';
import { ChatProvider, FeaturesProvider } from '@gtintel/platform-context';
import { RootStore } from '@gtintel/platform-stores';
import {
  clearSessionStorage,
  getSessionStorageItem,
  setSessionStorageItem,
  useCookieToSessionStorage,
  useRestoreSessionFromAuth,
} from '@gtintel/platform-utils';

import HeapAnalytics from '../components/HeapAnalytics';
import GlobalStyles from '../styles/global';
import { styletron } from '../styletron';
import './app.css';

const trackerDomains = ['.heapanalytics.com', 'lr-in-prod.com', 'sentry.io'];
const authUrls = ['api/token', 'api/2fa/token-verify'];

// Public pages that don't require session restoration
const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/login/users',
  '/login/callback',
  '/password-reset',
  '/auth/callback/',
  '/auth/set_password',
  '/sso',
];

if (!['dev', 'local'].includes(process.env.EXECUTION_ENV)) {
  LogRocket.init(process.env.LOGROCKET_APP_KEY, {
    release: String(process.env.SENTRY_RELEASE),
    dom: {
      textSanitizer: process.env.EXECUTION_ENV === 'prod',
      inputSanitizer: process.env.EXECUTION_ENV === 'prod',
    },
    network: {
      requestSanitizer: (request) => {
        const urlLowerCase = request.url.toLowerCase();

        if (authUrls.some((url) => urlLowerCase.includes(url))) {
          request.body = null;
        }

        if (trackerDomains.some((item) => urlLowerCase.includes(item))) {
          return null;
        }

        return request;
      },
    },
  });
}

// Wrapper component to use hooks
function CookieMigration() {
  useCookieToSessionStorage(COOKIE_NAMES.INFOR_USER);
  useCookieToSessionStorage(COOKIE_NAMES.TYPE_VIEW);
  useCookieToSessionStorage(COOKIE_NAMES.TWILIO_USER_TOKEN);
  return null;
}

// Component to restore session from auth cookies when opening new tabs
function SessionRestoration({ children }) {
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  // Enable client-side rendering only after mount to avoid SSR hydration
  useEffect(() => {
    setIsClient(true);

    // Check if sessionStorage has required values
    const checkSession = () => {
      try {
        const hasUserInfo = !!sessionStorage.getItem(COOKIE_NAMES.INFOR_USER);
        const hasTypeView = !!sessionStorage.getItem(COOKIE_NAMES.TYPE_VIEW);
        return hasUserInfo && hasTypeView;
      } catch {
        return false;
      }
    };

    setHasSession(checkSession());
  }, []);

  // Skip session restoration on public auth pages
  const isPublicRoute = PUBLIC_ROUTES.some((route) => {
    // For root path, only match exactly to avoid matching all paths
    if (route === '/') {
      return router.pathname === '/';
    }
    // For other paths, use exact match or startsWith for sub-routes
    return router.pathname === route || router.pathname.startsWith(route);
  });

  const restoreSession = useCallback(async () => {
    try {
      // Fetch user data from backend using JWT token
      const userData = await getCurrentUserAction(true);

      // Fetch Twilio token
      const twilioData = await getTwilioToken();
      setSessionStorageItem(COOKIE_NAMES.TWILIO_USER_TOKEN, twilioData.token);

      // Set type_view based on user role (for dual accounts, frontend typically defaults to the first role)
      if (userData && userData.length > 0) {
        const user = userData[0];
        if (user.is_administrator) {
          setSessionStorageItem(COOKIE_NAMES.TYPE_VIEW, ADMIN_ROLE);
        } else if (user.is_network_partner) {
          setSessionStorageItem(COOKIE_NAMES.TYPE_VIEW, PARTNER_ROLE);
        } else if (user.is_client) {
          setSessionStorageItem(COOKIE_NAMES.TYPE_VIEW, CLIENT_ROLE);
        } else {
          clearSessionStorage();
          throw new Error('Unknown user type');
        }
      }
    } catch (error) {
      console.error('[SessionRestoration] Failed to restore session:', error);
      throw error;
    }
  }, []);

  const isRestoring = useRestoreSessionFromAuth(
    {
      authToken: COOKIE_NAMES.TOKEN,
      inforUser: COOKIE_NAMES.INFOR_USER,
      typeView: COOKIE_NAMES.TYPE_VIEW,
      twilioToken: COOKIE_NAMES.TWILIO_USER_TOKEN,
    },
    restoreSession,
    isClient && !hasSession && !isPublicRoute // Only restore when client-side, no session, and not public route
  );

  // Monitor session changes during restoration
  useEffect(() => {
    if (!isClient || hasSession || isPublicRoute || !isRestoring) {
      return;
    }

    const checkInterval = setInterval(() => {
      try {
        const hasUserInfo = !!sessionStorage.getItem(COOKIE_NAMES.INFOR_USER);
        const hasTypeView = !!sessionStorage.getItem(COOKIE_NAMES.TYPE_VIEW);
        if (hasUserInfo && hasTypeView) {
          setHasSession(true);
        }
      } catch {
        // Ignore errors
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [isClient, hasSession, isPublicRoute, isRestoring]);

  // Skip restoration on public routes
  if (isPublicRoute) {
    return children;
  }

  // Don't render until client-side to avoid SSR hydration issues
  if (!isClient) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  // Show loading state while restoring
  if (!hasSession && isRestoring) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
}

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    const mobxStore = new RootStore();
    ctx.mobxStore = mobxStore;

    return {
      pageProps: Component.getInitialProps
        ? await Component.getInitialProps(ctx)
        : {},
    };
  }

  constructor(props) {
    super(props);
    this.mobxStore = new RootStore();
  }

  componentDidMount() {
    const user = getSessionStorageItem(COOKIE_NAMES.INFOR_USER);
    if (user?.id) {
      LogRocket.identify(String(user.id), {
        name: user.full_name,
        email: user.email,
      });
    }
  }

  render() {
    const { Component, pageProps } = this.props;
    const TIMEOUT = 300;

    return (
      <>
        <CookieMigration />
        <SessionRestoration>
          <NextNprogress
            color="#ffba00"
            startPosition={0.3}
            stopDelayMs={200}
            height={3}
            showOnShallow={true}
            options={{ showSpinner: false }}
          />
          <FeaturesProvider>
            <ChatProvider>
              <Provider {...this.mobxStore}>
                {/* <Provider session={pageProps.session}> */}
                <StyletronProvider value={styletron}>
                  <BaseProvider theme={LightTheme}>
                    <Component {...pageProps} />
                    <ToasterContainer
                      autoHideDuration={3000}
                      placement={PLACEMENT.bottomRight}
                    ></ToasterContainer>
                  </BaseProvider>
                </StyletronProvider>
                <HeapAnalytics />
                {/* </Provider> */}
              </Provider>
            </ChatProvider>
          </FeaturesProvider>
        </SessionRestoration>

        <style jsx global>
          {`
            .page-transition-opacity-enter {
              opacity: 0.01;
            }
            .page-transition-opacity-enter-active {
              opacity: 1;
              transition: opacity 300ms;
            }
            .page-transition-opacity-exit {
              opacity: 1;
            }
            .page-transition-opacity-exit-active {
              opacity: 0.01;
              transition: opacity 300ms;
            }

            .page-transition-enter {
              opacity: 0;
              transform: translate3d(0, 20px, 0);
            }
            .page-transition-enter-active {
              opacity: 1;
              transform: translate3d(0, 0, 0);
              transition: opacity ${TIMEOUT}ms, transform ${TIMEOUT}ms;
            }
            .page-transition-exit {
              opacity: 1;
            }
            .page-transition-exit-active {
              opacity: 0.01;
              transition: opacity ${TIMEOUT}ms;
            }

            .loading-indicator-appear,
            .loading-indicator-enter {
              opacity: 0;
            }
            .loading-indicator-appear-active,
            .loading-indicator-enter-active {
              opacity: 1;
              transition: opacity ${TIMEOUT}ms;
            }
          `}
        </style>
        <GlobalStyles />
      </>
    );
  }
}

export default MyApp;
